import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import {
    Check,
    ClipboardList,
    Copy,
    ExternalLink,
    Mail,
    Receipt,
    Ticket,
    User,
    X,
} from 'lucide-react';
import Tabs from 'components/_UI/Tabs';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import { copyToClipboard } from 'helpers/clipboard';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import indexPriority from 'theme/z-index';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { chipOf as orderChipOf } from '../orders/orderTokens';
import {
    statusChip,
    statusOf,
    canCheckIn,
    typeLabel,
    formatDateTime,
    PARTICIPANT_STATUS,
} from './participantTokens';

const TAB_GENERAL = 'general';
const TAB_CUSTOM = 'custom';

/**
 * Detalhe do participante, aberto ao clicar na linha.
 *
 * Não usa o Modal/ModalV2 compartilhados: nos tamanhos que comportam o conteúdo
 * eles viram uma folha de meia tela, quase sem cantos, sobre um fundo 90% preto.
 * Aqui é um cartão de largura limitada, cantos de 20px e fundo translúcido —
 * a lista continua visível atrás, e a modal lê como "um olhar rápido", não como
 * uma troca de tela. Cabeçalho e rodapé ficam fixos e só o corpo rola: quem
 * está na porta confere nome e status sem perder o botão de fechar. No celular
 * vira bottom sheet.
 *
 * O portal é necessário porque a aba vive dentro do modal de detalhe do evento.
 *
 * Campos adicionais só ganham aba quando existem — uma aba que sempre abre
 * vazia ensina o produtor a não clicar nela.
 */
export default function ParticipantDetailModal({ participant, pendingUndo, checkinOpen = true, onCheckIn, onClose }) {
    const [tab, setTab] = useState(TAB_GENERAL);

    const customFields = useMemo(() => readCustomFields(participant?.custom_fields), [participant]);

    // Cada participante abre na aba geral; sem isso, ir de alguém com campos
    // adicionais para alguém sem deixaria a modal numa aba que não existe.
    useEffect(() => {
        setTab(TAB_GENERAL);
    }, [participant?.code]);

    const open = Boolean(participant);
    const closeRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Esc fecha, a página atrás para de rolar e o foco entra na modal — e volta
    // para a linha de onde saiu quando ela fecha.
    useEffect(() => {
        if (!open) return undefined;

        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onCloseRef.current();
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        closeRef.current?.focus();

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
            previousFocus?.focus?.();
        };
    }, [open]);

    if (!participant) return null;

    const status = pendingUndo ? PARTICIPANT_STATUS.USED : statusOf(participant);
    const chip = statusChip[status];
    const checkinAllowed = canCheckIn(participant) && !pendingUndo && checkinOpen;
    const isTicket = participant.type === 'ticket';
    const orderChip = participant.order_status ? orderChipOf(participant.order_status) : null;

    const tabs = [
        { name: TAB_GENERAL, label: 'Detalhes gerais', icon: User },
        ...(customFields.length > 0
            ? [{
                name: TAB_CUSTOM,
                icon: ClipboardList,
                label: (
                    <>
                        Campos adicionais
                        <TabCount>{customFields.length}</TabCount>
                    </>
                ),
            }]
            : []),
    ];

    return ReactDOM.createPortal(
        <Overlay onClick={onClose}>
            <Dialog
                role='dialog'
                aria-modal='true'
                aria-labelledby='participant-detail-name'
                onClick={(event) => event.stopPropagation()}
            >
                <SheetHandle aria-hidden='true' />
                <Header>
                    <HeaderTop>
                        <DynamicProfileImage
                            img={participant.avatar}
                            word={participant.name}
                            size='48px'
                            textSize='highlight'
                            bgColor={panelAccent(colors.secondaryPurple)}
                        />

                        <Identity>
                            <Name id='participant-detail-name'>{participant.name}</Name>
                            {participant.email && (
                                <Email>
                                    <Mail size={13} aria-hidden='true' />
                                    <span>{participant.email}</span>
                                </Email>
                            )}
                            <Tags>
                                <StatusChip $chip={chip}>
                                    {status === PARTICIPANT_STATUS.USED && (
                                        <Check size={11} strokeWidth={3} aria-hidden='true' />
                                    )}
                                    {chip.label}
                                </StatusChip>
                                <TypeTag>{typeLabel[participant.type] || participant.type}</TypeTag>
                            </Tags>
                        </Identity>

                        <CloseButton ref={closeRef} type='button' onClick={onClose} aria-label='Fechar detalhes'>
                            <X size={18} aria-hidden='true' />
                        </CloseButton>
                    </HeaderTop>

                    {tabs.length > 1 && (
                        <Tabs key={participant.code} tabs={tabs} onChangeTab={setTab} />
                    )}
                </Header>

                <Body>
                    {tab === TAB_GENERAL ? (
                        <>
                            <Section>
                                <SectionTitle>
                                    <Ticket size={14} aria-hidden='true' />
                                    {isTicket ? 'Ingresso' : 'Produto'}
                                </SectionTitle>
                                <Card>
                                    <FieldGrid>
                                        <Field label='Item' value={participant.product} wide />
                                        <Field label='Código de uso' value={participant.code} mono copyable />
                                        <Field
                                            label={isTicket ? 'Check-in' : 'Retirada'}
                                            value={checkinStateLabel(status, isTicket)}
                                        />
                                    </FieldGrid>
                                </Card>
                            </Section>

                            <Section>
                                <SectionTitle>
                                    <Receipt size={14} aria-hidden='true' />
                                    Compra
                                </SectionTitle>
                                <Card>
                                    <FieldGrid>
                                        <Field label='Data da compra' value={formatDateTime(participant.created_at)} />
                                        <Field
                                            label='Pedido'
                                            value={orderChip && <OrderChip $chip={orderChip}>{orderChip.label}</OrderChip>}
                                        />
                                        <Field label='Cupom' value={participant.coupon} mono />
                                        <Field label='Nome do comprador' value={participant.buyer?.name} wide />
                                        <Field label='E-mail do comprador' value={participant.buyer?.email} copyable wide />
                                        <Field label='Telefone do comprador' value={formatPhone(participant.buyer?.phone)} mono copyable />
                                    </FieldGrid>
                                </Card>
                            </Section>

                            <Section>
                                <SectionTitle>
                                    <User size={14} aria-hidden='true' />
                                    Participante
                                </SectionTitle>
                                <Card>
                                    <FieldGrid>
                                        <Field label='Nome' value={participant.name} wide />
                                        <Field label='E-mail' value={participant.email} copyable wide />
                                        <Field label='CPF' value={formatDocument(participant.document)} mono />
                                    </FieldGrid>
                                </Card>
                            </Section>
                        </>
                    ) : (
                        <Section>
                            <SectionTitle>
                                <ClipboardList size={14} aria-hidden='true' />
                                Respostas do formulário
                            </SectionTitle>
                            <AnswerList>
                                {customFields.map(({ label, value }) => (
                                    <Answer key={label}>
                                        <AnswerLabel>{label}</AnswerLabel>
                                        <AnswerValue value={value} />
                                    </Answer>
                                ))}
                            </AnswerList>
                        </Section>
                    )}
                </Body>

                <Footer>
                    {checkinAllowed ? (
                        <CheckinButton type='button' onClick={() => onCheckIn(participant)}>
                            <Check size={16} strokeWidth={3} aria-hidden='true' />
                            {isTicket ? 'Fazer check-in' : 'Validar retirada'}
                        </CheckinButton>
                    ) : (
                        <FooterNote>{footerNote(status, isTicket, pendingUndo, checkinOpen)}</FooterNote>
                    )}
                </Footer>
            </Dialog>
        </Overlay>,
        document.body
    );
}

function Field({ label, value, mono, copyable, wide }) {
    const [copied, setCopied] = useState(false);
    const empty = value === null || value === undefined || value === '' || value === false;

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 1800);
        return () => clearTimeout(timer);
    }, [copied]);

    return (
        <FieldBox $wide={wide}>
            <FieldLabel>{label}</FieldLabel>
            <FieldValueRow>
                <FieldValue $mono={mono && !empty} $empty={empty}>
                    {empty ? 'Não informado' : value}
                </FieldValue>
                {copyable && !empty && typeof value === 'string' && (
                    <CopyButton
                        type='button'
                        onClick={() => copyToClipboard(value, () => setCopied(true))}
                        aria-label={copied ? `${label} copiado` : `Copiar ${label.toLowerCase()}`}
                        title={copied ? 'Copiado' : 'Copiar'}
                        $copied={copied}
                    >
                        {copied
                            ? <Check size={13} strokeWidth={3} aria-hidden='true' />
                            : <Copy size={13} aria-hidden='true' />}
                    </CopyButton>
                )}
            </FieldValueRow>
        </FieldBox>
    );
}

/**
 * As respostas chegam como o comprador as deixou: texto, lista (múltipla
 * escolha), booleano ou um link solto. Cada forma tem sua leitura — uma lista
 * vira chips em vez de "a, b, c", e link vira link de verdade.
 */
function AnswerValue({ value }) {
    if (isBlank(value)) return <AnswerText $empty>Não respondido</AnswerText>;

    if (Array.isArray(value)) {
        return (
            <AnswerChips>
                {value.map((item, index) => <AnswerChip key={`${item}-${index}`}>{String(item)}</AnswerChip>)}
            </AnswerChips>
        );
    }

    if (typeof value === 'boolean') return <AnswerText>{value ? 'Sim' : 'Não'}</AnswerText>;

    if (typeof value === 'object') return <AnswerText>{JSON.stringify(value)}</AnswerText>;

    const text = String(value);
    const href = linkOf(text);

    if (href) {
        return (
            <AnswerLink href={href} target='_blank' rel='noopener noreferrer'>
                <span>{hostOf(href)}</span>
                <ExternalLink size={13} aria-hidden='true' />
            </AnswerLink>
        );
    }

    return <AnswerText>{text}</AnswerText>;
}

/* ─────────────────────────────  Leitura  ───────────────────────────── */

function readCustomFields(raw) {
    if (!raw || typeof raw !== 'object') return [];

    if (Array.isArray(raw)) {
        return raw
            .filter((item) => item && (item.label || item.name))
            .map((item) => ({ label: item.label || item.name, value: item.value }));
    }

    return Object.entries(raw).map(([label, value]) => ({ label, value }));
}

function isBlank(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

function linkOf(text) {
    const trimmed = text.trim();
    if (/^https?:\/\/\S+$/i.test(trimmed)) return trimmed;
    if (/^www\.\S+$/i.test(trimmed)) return `https://${trimmed}`;
    return null;
}

function hostOf(href) {
    try {
        const url = new URL(href);
        return `${url.hostname}${url.pathname !== '/' ? url.pathname : ''}`;
    } catch (error) {
        return href;
    }
}

function formatDocument(document) {
    const digits = String(document || '').replace(/\D/g, '');
    if (digits.length !== 11) return document;
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function formatPhone(phone) {
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return phone;
}

function checkinStateLabel(status, isTicket) {
    if (status === PARTICIPANT_STATUS.USED) return isTicket ? 'Realizado' : 'Retirado';
    if (status === PARTICIPANT_STATUS.REFUNDED) return 'Indisponível (estornado)';
    return isTicket ? 'Ainda não realizado' : 'Ainda não retirado';
}

function footerNote(status, isTicket, pendingUndo, checkinOpen) {
    if (pendingUndo) return 'Check-in registrado. Dá para desfazer pela lista nos próximos segundos.';
    if (status === PARTICIPANT_STATUS.USED) return isTicket ? 'Este ingresso já fez check-in.' : 'Este produto já foi retirado.';
    // Check-in por dia, com a lista em outro dia: o check-in só vale para hoje.
    if (status === PARTICIPANT_STATUS.ACTIVE && !checkinOpen) return 'O check-in só pode ser feito no dia de hoje. Com a lista em outro dia, ela serve só para consulta.';
    return 'Este item foi estornado e não pode receber check-in.';
}

/* ─────────────────────────────  Estilos  ───────────────────────────── */

const ACCENT = panelAccent(colors.secondaryPurple);
const HAIRLINE = 'rgba(16, 24, 40, 0.09)';

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const popIn = keyframes`
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

/* Véu translúcido em vez do preto 90% dos modais antigos: a lista continua
   reconhecível atrás, então a modal lê como um detalhe sobre ela. */
const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${indexPriority.modal};
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(16, 24, 40, 0.45);
    backdrop-filter: blur(3px);
    animation: ${fadeIn} 0.18s ease both;

    @media screen and (min-width: ${breakpoints.smd}) {
        align-items: center;
        padding: 32px 24px;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Dialog = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 92dvh;
    overflow: hidden;
    text-align: left;
    background: ${colors.neutralLight};
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -8px 32px rgba(16, 24, 40, 0.14);
    animation: ${slideUp} 0.28s cubic-bezier(0.32, 0.72, 0, 1) both;
    padding-bottom: env(safe-area-inset-bottom, 0px);

    @media screen and (min-width: ${breakpoints.smd}) {
        max-width: 560px;
        max-height: min(760px, calc(100dvh - 64px));
        border-radius: 20px;
        padding-bottom: 0;
        box-shadow:
            0 24px 64px rgba(16, 24, 40, 0.18),
            0 2px 8px rgba(16, 24, 40, 0.06);
        animation: ${popIn} 0.2s ease-out both;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const SheetHandle = styled.div`
    flex-shrink: 0;
    width: 40px;
    height: 4px;
    margin: 10px auto 0;
    border-radius: 2px;
    background: rgba(16, 24, 40, 0.18);

    @media screen and (min-width: ${breakpoints.smd}) {
        display: none;
    }
`;

const Header = styled.header`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 20px 16px;
    background: ${colors.neutralLight};
    border-bottom: 1px solid ${HAIRLINE};

    @media screen and (min-width: ${breakpoints.smd}) {
        padding: 24px 24px 16px;
    }
`;

const HeaderTop = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const Identity = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Name = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: ${colors.nightDark};
    word-break: break-word;
`;

const Email = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 13px;
    color: ${colors.night};
    min-width: 0;

    & > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    & > svg {
        flex-shrink: 0;
    }
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
`;

const StatusChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    padding: 0 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: ${({ $chip }) => $chip.bg};
    color: ${({ $chip }) => $chip.text};
    border: 1px solid ${({ $chip }) => $chip.border};
`;

const OrderChip = styled(StatusChip)`
    height: 20px;
    padding: 0 8px;
`;

const TypeTag = styled.span`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 6px;
    background: ${colors.neutralBackground};
    border: 1px solid rgba(16, 24, 40, 0.08);
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night};
`;

const CloseButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin: -4px -6px 0 0;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
        background: ${colors.neutralBackground};
        color: ${colors.nightDark};
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
    }
`;

const TabCount = styled.span`
    min-width: 20px;
    padding: 1px 6px;
    border-radius: 999px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '1F')};
    font-size: 11px;
    font-weight: 700;
    text-align: center;
`;

const Body = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    background: #FAFAFB;

    @media screen and (min-width: ${breakpoints.smd}) {
        padding: 20px 24px 24px;
    }
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
`;

const SectionTitle = styled.h3`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Card = styled.div`
    padding: 16px;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.08);
`;

const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px 20px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr 1fr;
    }
`;

const FieldBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
    }
`;

const FieldLabel = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${colors.night};
`;

const FieldValueRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const FieldValue = styled.span`
    min-width: 0;
    font-size: 14px;
    font-weight: ${({ $empty }) => ($empty ? 400 : 600)};
    font-style: ${({ $empty }) => ($empty ? 'italic' : 'normal')};
    color: ${({ $empty }) => ($empty ? colors.night : colors.nightDark)};
    word-break: break-word;

    ${({ $mono }) => $mono && `
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        letter-spacing: 0.4px;
    `}
`;

const CopyButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    border: 1px solid ${({ $copied }) => ($copied ? '#B7E5C9' : 'rgba(16, 24, 40, 0.1)')};
    background: ${({ $copied }) => ($copied ? '#EDF9F2' : colors.neutralLight)};
    color: ${({ $copied }) => ($copied ? '#0F6B31' : colors.night)};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
        color: ${({ $copied }) => ($copied ? '#0F6B31' : ACCENT)};
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
    }
`;

const AnswerList = styled.dl`
    margin: 0;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.08);
    overflow: hidden;
`;

/* Pergunta em cima, resposta embaixo: as perguntas do formulário costumam ser
   frases inteiras ("Você tem alguma restrição alimentar?"), e espremê-las numa
   coluna de rótulo cortava justamente a parte que diz o que foi perguntado. */
const Answer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 16px;

    & + & {
        border-top: 1px solid ${HAIRLINE};
    }
`;

const AnswerLabel = styled.dt`
    font-size: 12px;
    font-weight: 500;
    color: ${colors.night};
    word-break: break-word;
`;

const AnswerText = styled.dd`
    margin: 0;
    font-size: 14px;
    font-weight: ${({ $empty }) => ($empty ? 400 : 600)};
    font-style: ${({ $empty }) => ($empty ? 'italic' : 'normal')};
    color: ${({ $empty }) => ($empty ? colors.night : colors.nightDark)};
    white-space: pre-wrap;
    word-break: break-word;
`;

const AnswerChips = styled.dd`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 2px 0 0;
`;

/* Sem fundo colorido: a resposta é dado, não estado — cor aqui competia com
   o chip de status no cabeçalho. A borda só separa uma opção da outra. */
const AnswerChip = styled.span`
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid rgba(16, 24, 40, 0.14);
    color: ${colors.nightDark};
    font-size: 12px;
    font-weight: 600;
`;

const AnswerLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    align-self: flex-start;
    max-width: 100%;
    font-size: 14px;
    font-weight: 600;
    color: ${ACCENT};
    text-decoration: underline;
    text-underline-offset: 2px;

    & > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    & > svg {
        flex-shrink: 0;
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

const Footer = styled.footer`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 14px 20px;
    background: ${colors.neutralLight};
    border-top: 1px solid ${HAIRLINE};

    @media screen and (min-width: ${breakpoints.smd}) {
        padding: 14px 24px;
    }
`;

const FooterNote = styled.p`
    flex: 1;
    margin: 0;
    font-size: 13px;
    color: ${colors.night};
`;

/* Mesmo botão da linha, maior: no celular ele é o alvo de toque da modal. */
const CheckinButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 44px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid ${ACCENT};
    background: ${ACCENT};
    color: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.15s ease;

    &:hover {
        filter: brightness(1.1);
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
    }

    @media screen and (min-width: ${breakpoints.sm}) {
        width: auto;
    }
`;
